import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';


const LAP_COLORS = [
    { stroke: '#6366F1', glow: 'rgba(99,102,241,0.55)', bg: 'rgba(99,102,241,0.12)', text: '#6366F1' },
    { stroke: '#4F46E5', glow: 'rgba(79,70,229,0.55)', bg: 'rgba(79,70,229,0.12)', text: '#4F46E5' },
    { stroke: '#7C3AED', glow: 'rgba(124,58,237,0.55)', bg: 'rgba(124,58,237,0.12)', text: '#7C3AED' },
    { stroke: '#8B5CF6', glow: 'rgba(139,92,246,0.55)', bg: 'rgba(139,92,246,0.12)', text: '#8B5CF6' },
    { stroke: '#A78BFA', glow: 'rgba(167,139,250,0.55)', bg: 'rgba(167,139,250,0.12)', text: '#A78BFA' },
    { stroke: '#D946EF', glow: 'rgba(217,70,239,0.55)', bg: 'rgba(217,70,239,0.12)', text: '#D946EF' },
    { stroke: '#E879F9', glow: 'rgba(232,121,249,0.55)', bg: 'rgba(232,121,249,0.12)', text: '#E879F9' },
    { stroke: '#F472B6', glow: 'rgba(244,114,182,0.55)', bg: 'rgba(244,114,182,0.12)', text: '#F472B6' },
    { stroke: '#FB923C', glow: 'rgba(251,146,60,0.55)', bg: 'rgba(251,146,60,0.12)', text: '#FB923C' },
    { stroke: '#F97316', glow: 'rgba(249,115,22,0.55)', bg: 'rgba(249,115,22,0.12)', text: '#F97316' },
    { stroke: '#EF4444', glow: 'rgba(239,68,68,0.55)', bg: 'rgba(239,68,68,0.12)', text: '#EF4444' },
    { stroke: '#DC2626', glow: 'rgba(220,38,38,0.55)', bg: 'rgba(220,38,38,0.12)', text: '#DC2626' },
];

const getLap = (elapsed, total) => {
    if (elapsed === 0) return { lap: 0, within: 0 };
    const lap = Math.floor(elapsed / total);
    const within = (elapsed % total) / total;
    return { lap, within };
};

const colorForLap = (lap) => {
    if (lap <= 0) return LAP_COLORS[0];
    return LAP_COLORS[Math.min(lap, LAP_COLORS.length - 1)];
};

const CircularTimer = ({ formattedTime, formattedHours, status, elapsedTime, totalSeconds }) => {
    const theme = useTheme();

    const size = 280;
    const strokeWidth = 6;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;

    const { lap, within } = getLap(elapsedTime, totalSeconds);
    const progressPct = lap === 0 && elapsedTime === 0 ? 0 : within * 100;
    const offset = circumference - (progressPct / 100) * circumference;
    const color = colorForLap(lap);

    if (!color) {
        return null;
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 2,
            }}
        >
            <Box sx={{ position: 'relative', width: size, height: size }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: size * 0.7,
                        height: size * 0.7,
                        borderRadius: '50%',
                        background: status === 'running'
                            ? `radial-gradient(circle, ${color.bg} 0%, transparent 70%)`
                            : 'none',
                        transition: 'background 0.8s ease',
                    }}
                />
                <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', overflow: 'visible' }}>
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke={color.bg}
                        strokeWidth={strokeWidth}
                        style={{ transition: 'stroke 0.8s ease' }}
                    />
                    {lap > 0 && (
                        <circle
                            cx={size / 2}
                            cy={size / 2}
                            r={radius}
                            fill="none"
                            stroke={color.stroke}
                            strokeWidth={strokeWidth}
                            strokeDasharray={circumference}
                            strokeDashoffset={0}
                            opacity={0.25}
                            style={{ transition: 'stroke 0.8s ease' }}
                        />
                    )}
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke={color.stroke}
                        strokeWidth={strokeWidth}
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                        strokeLinecap="round"
                        style={{
                            transition: 'stroke-dashoffset 0.5s ease, stroke 0.8s ease',
                            filter: `drop-shadow(0 0 ${theme.palette.mode === 'dark' ? '8px' : '4px'} ${color.glow})`,
                        }}
                    />
                </svg>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: '"Inter", monospace',
                            fontSize: '3rem',
                            fontWeight: 700,
                            color: color.text,
                            letterSpacing: '0.05em',
                            fontVariantNumeric: 'tabular-nums',
                            textShadow: theme => theme.palette.mode === 'dark' ? `0 0 20px ${color.glow}` : `0 0 5px ${color.glow}`,
                            transition: 'color 0.8s ease',
                        }}
                    >
                        {formattedTime}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: '#9aa0a6',
                            letterSpacing: '0.15em',
                            textTransform: 'uppercase',
                        }}
                    >
                        {formattedHours}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default CircularTimer;
